<template>
  <div
    class="pros-collection"
    :class="{
      [customStyleClass]: customStyleClass,
      'rating-size-small': ratingSizeSmall,
      'prod-name-one-line': lineClamp === 1,
      'prod-name-two-line': lineClamp === 2,
      [`pros-collection-${recommendationConfig?.zone}`]: true
    }"
  >
    <component
      :is="prosComponent"
      v-if="isRecommendationDataAvailable && resolvedComponentType"
      :key="workflow"
      :config-data="getConfigData"
      @dataLoadStatusEvent="(e) => $emit('dataLoadStatusEvent', e)"
    />
  </div>
</template>

<script>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useWishlistLoader } from '@common-vue/product-thumbnail';
import { useDebounceFn } from '@vueuse/core';
import { prosCollectionZoneConfigMap } from '../utils/prosCollectionZoneConfigMap';
import prosCollectionComponentType from '../utils/prosCollectionComponentType';
import constants from '../utils/constants';
import {
  merge,
  getRecentlyViewedItems,
  getDeviceType,
  isKillSwitchTrue,
  removeUndefinedValuesFromObject,
  isMcom,
  filterGWPAndPWPProducts,
  getURLParameter,
  addRecentlyViewedItems,
  onElementWillBeVisible,
  getImageId,
  getProductColorMap,
  isPdpZone,
  isMobile,
  getTitle,
  getGuids,
} from '../utils/utils';
import { updateConfig, useProsCollection } from '../composables/useProsCollection';
import {
  isZoneInBrowseList,
  useExperimentationForBrowse,
  killswitchFromZoneName,
} from '../composables/useExperimentationForBrowse';
import useExperimentationForRvi from '../composables/useExperimentationForRvi';
import getWorkflow from '../store_modules/storeHelper';
import prosZones from '../utils/prosZones';
import {
  checkExperimentation,
  setKillSwitchUsingExperimentation,
  checkExperimentationRecipeIds,
} from '../utils/killSwitchUtil';
import lazyloadConfig from '../utils/lazyloadConfig.json';
import { minDetailProductThumbnail } from '../utils/productTransformer';
import ProductPoolCarousel from './ProductPoolCarousel/ProductPoolCarousel';
import ProsCarousel from './ProsCarousel/ProsCarousel';
import ProsVertical from './ProsVertical/ProsVertical';
import ProsBeautyRegimen from './ProsBeautyRegimen/ProsBeautyRegimen';
import ProsContentCarousel from './ProsContentCarousel/ProsContentCarousel';
import ProsContentCollection from './ProsContentCollection/ProsContentCollection';
import ProsDashboard from './ProsDashboard/ProsDashboard';
import ProsFbt from './ProsFbt/ProsFbt';
import ProsGrid from './ProsGrid/ProsGrid';
import RviCarousel from './RviCarousel/RviCarousel';
import { SET_PAGE_ID } from '../type/mutations';
// eslint-disable-next-line import/no-dynamic-require
require(`../scss/${process.env.ENTRY_POINT}/pros-collection.scss`);

const {
  PRODUCT_POOL_CAROUSEL,
  PROS_BEAUTY_REGIMEN,
  PROS_CONTENT_CAROUSEL,
  PROS_CONTENT_COLLECTION,
  PROS_DASHBOARD,
  PROS_FBT,
  PROS_GRID,
  PROS_VERTICAL,
  RVI_CAROUSEL,
} = prosCollectionComponentType;

const COMPONENT_TYPE_BY_NAMES = {
  [PRODUCT_POOL_CAROUSEL]: ProductPoolCarousel,
  [PROS_BEAUTY_REGIMEN]: ProsBeautyRegimen,
  [PROS_CONTENT_CAROUSEL]: ProsContentCarousel,
  [PROS_CONTENT_COLLECTION]: ProsContentCollection,
  [PROS_DASHBOARD]: ProsDashboard,
  [PROS_FBT]: ProsFbt,
  [PROS_GRID]: ProsGrid,
  [PROS_VERTICAL]: ProsVertical,
  [RVI_CAROUSEL]: RviCarousel,
};

const { MIN_ITEMS_AMOUNT_FOR_DASHBOARD_RVI, UPC_BOPS_PURCHASABLE } = constants;
const { SMALL, LARGE } = constants.BREAK_POINT_TYPE;
const {
  PROS, RVI, POOL, DASHBOARD,
} = constants.VIEW_TYPE;
const { BCOM } = constants.ENTRY_POINT;
const {
  PDP_PAGE,
  SEARCH_PAGE,
  BROWSE_PAGE,
  BAG_PAGE,
} = prosZones;

export default {
  name: 'ProsCollection',
  props: {
    panelConfig: {
      type: Object,
      default: () => ({}),
    },
    recommendationConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['dataLoadStatusEvent'],
  setup() {
    const store = useStore();
    const zoneRecommendationMap = computed(() => ({
      ...store.state.recommendationStore.zoneRecommendationMap,
    }));

    const wishlistLoaded = computed(() => store.state.recommendationStore.wishlistLoaded);
    // load wishlist data once on page
    if (!wishlistLoaded.value) {
      useWishlistLoader(); // eslint-disable-line no-unused-vars
    }
    const entryPoint = process.env.ENTRY_POINT;
    return { entryPoint, zoneRecommendationMap, wishlistLoaded };
  },
  data() {
    return {
      bagItems: this.panelConfig.bagItems,
      configData: {},
      customHeaderType: null,
      deviceType: SMALL,
      extraConfig: {},
      extraRowGap: null,
      maxProduct: null,
      zoneLevelCustomClass: null,
      headerTabIndex: null,
      headerAriaLabel: null,
      filterFetchedProducts: undefined,
      getLinkUrl: this.panelConfig.getLinkUrl,
      isRecommendationDataAvailable: false,
      linkName: this.panelConfig.linkName,
      linkUrl: this.panelConfig.linkUrl,
      loading: true,
      productIds: null,
      properties: { ...this.panelConfig.properties },
      recommendationData: null,
      resolvedComponentType: this.panelConfig.componentType,
      resolvedNumberOfItems: this.panelConfig.numberOfItems,
      resolvedPanelType: this.panelConfig.panelType || PROS,
      rviDependent: this.panelConfig.rviDependent || false,
      customStyleClass: this.panelConfig.customStyleClass,
      transformer:
        this.panelConfig.transformer
        || (this.entryPoint === BCOM ? minDetailProductThumbnail : undefined),
      useWishList: true,
      workflow: null,
      zone: this.recommendationConfig.zone,
      loadRTODebounced: null,
    };
  },
  computed: {
    isShowCornerBadge() {
      return isMcom() && this.deviceType === SMALL;
    },
    prosComponent() {
      // replace with LoadingComponent
      if (!this.resolvedComponentType || !this.isRecommendationDataAvailable) {
        return 'div';
      }
      if (this.isZoneEnabled(this.zone)) {
        return COMPONENT_TYPE_BY_NAMES[this.resolvedComponentType] || ProsCarousel;
      }
      return 'div';
    },
    getConfigData() {
      const title = getTitle({
        title: this.panelConfig?.title,
        zone: this.zone,
        headerText: this.recommendationData?.headerText,
      });

      const useWishList = this.panelConfig?.useWishList !== undefined
        ? this.panelConfig?.useWishList : isMcom() || this.properties.isHeartFunctionallyEnabled;

      return removeUndefinedValuesFromObject({
        linkName: this.linkName,
        linkUrl: this.linkUrl,
        numberOfItems: this.resolvedNumberOfItems,
        productIds: this.productIds,
        productDescriptionAlignment: this.panelConfig.productDescriptionAlignment,
        recommendationData: this.recommendationData,
        title: title ? decodeURIComponent(title.replace(/(<([^>]+)>)/gi, '')) : '',
        useWishList,
        deviceType: isMobile(),
        workflow: this.workflow,
        zone: this.zone,
        recommendationConfig: this.recommendationConfig,
        transformer: this.transformer,
        showCornerBadge: this.properties.showCornerBadge || this.isShowCornerBadge,
        extraRowGap: this.extraRowGap,
        maxProduct: this.maxProduct,
        zoneLevelCustomClass: this.zoneLevelCustomClass,
        headerTabIndex: this.headerTabIndex,
        headerAriaLabel: this.headerAriaLabel,
        customHeaderType: this.customHeaderType,
        filterFetchedProducts: this.filterFetchedProducts,
        openInNewTab: !isMobile() && this.properties.carouselOpenInNewTabEnabled,
        simplifiedThumbnail: this.properties.simplifiedThumbnailEnabled,
        showDivider: this.panelConfig.showDivider,
        properties: this.panelConfig.properties,
        analytics: this.panelConfig.analytics,
        showBopsOnlyProducts: this.showBopsOnlyProducts,
        isSimplificationOn: this.properties.isSimplificationOn,
        simplificationWithV1: this.properties.isSimplificationOnV2,
        simplificationNonBoldPercentageExp: this.properties.simplificationNonBoldPercentageExp,
        simplificationRemoveSaleExp: this.properties.simplificationRemoveSaleExp,
        selectedColor: this.extraConfig.selectedColor,
        selectedColorFamily: this.extraConfig.selectedColorFamily,
        enableColorRecommendations: this.properties.enableColorRecommendations,
        redesignCoreCarouselEnabled: this.properties.redesignCoreCarouselEnabled,
        consolidatedProductNameExp: this.properties.redesignCoreCarouselEnabled,
        colorSwatchEnhancement: this.properties.colorSwatchExp || this.properties.redesignCoreCarouselEnabled,
        showReviewCondensed: isMcom() && this.properties.redesignCoreCarouselEnabled,
      });
    },
    ratingSizeSmall() {
      return !['HP_ZONE_D', 'PDP_ZONE_A', 'PDP_ZONE_G'].includes(this.zone);
    },
    lineClamp() {
      if (this.properties.redesignCoreCarouselEnabled) {
        console.warn( // eslint-disable-line no-console
          '[ProdInfo] UX Experiment "redesignCoreCarouselEnabled" active: Single-line clamping is applied for product names on both Desktop and MEW as part of the carousel redesign experiment.',
          this.properties,
        );
        return 1; // one line both in case of Desktop or MEW (when experiment is on)
      }
      return this.deviceType === LARGE ? 1 : 2;
    },
    showBopsOnlyProducts() {
      return !!this.panelConfig.linkUrl?.toUpperCase().includes(UPC_BOPS_PURCHASABLE);
    },
  },
  watch: {
    zoneRecommendationMap(newValue, oldValue) {
      if (newValue[this.workflow] !== oldValue[this.workflow]) {
        this.recommendationData = newValue[this.workflow];
        this.updateRecommendationData();

        if (this.resolvedPanelType === PROS) {
          this.resolveProsType();
        }
        if (
          this.resolvedPanelType === DASHBOARD
          && this.productIds.length === 0
        ) {
          this.resolveDashboardType();
        }
        if (!this.linkUrl && this.getLinkUrl) {
          this.linkUrl = this.getLinkUrl({
            recommendationData: this.recommendationData
              ? { ...this.recommendationData }
              : {},
            recommendationConfig: { ...this.recommendationConfig },
          });
        }
      }
    },
    recommendationConfig(newValue, oldValue) {
      if (newValue.memberProductId !== oldValue.memberProductId) {
        this.loadRTODebounced();
      }

      if (this.properties.enableColorRecommendations
        && (newValue.selectedColor !== oldValue.selectedColor || newValue.selectedColorFamily !== oldValue.selectedColorFamily)) {
        this.extraConfig.selectedColor = newValue.selectedColor;
        this.extraConfig.selectedColorFamily = newValue.selectedColorFamily;

        this.loadRTODebounced();
      }
    },
    deviceType(newValue, oldValue) {
      if (newValue !== oldValue && this.resolvedPanelType === PROS) {
        this.resolveProsType();
      }
    },
  },
  async created() {
    if (typeof window !== 'undefined') {
      // TODO: need to confirm regarding KS
      const { Experimentation } = require('@vue-util/experimentation');
      if (this.zone && isZoneInBrowseList(this.zone)) {
        const { properties } = await useExperimentationForBrowse({
          zoneName: this.zone,
          _properties: this.properties,
          expSdk: Experimentation,
        });

        this.properties = properties;
      }
      await checkExperimentation(
        '2036-21',
        'simplifiedThumbnailEnabled',
        this.properties,
        Experimentation,
      );
      await checkExperimentation(
        '2483-22',
        'redesignCoreCarouselEnabled',
        this.properties,
        Experimentation,
      );

      // Only for Mcom
      if (isMcom()) {
        await checkExperimentation(
          '2483-21',
          'colorSwatchExp',
          this.properties,
          Experimentation,
        );
      }

      this.isExpNumberOfItemsMcomOn = isMcom() && this.properties.redesignCoreCarouselEnabled;

      this.priceSimplificationModeEnabled = this.properties.priceSimplificationEnabledSite
        || this.properties.priceSimplificationEnabled
        || this.properties.isPriceSimplificationEnabled;

      // for both Mcom & Bcom
      if (this.priceSimplificationModeEnabled) {
        const priceSimplificationRecipeIds = ['2054-21', '2054-22', '2054-23', '2054-24', '2054-25', '2054-26', '2054-27', ' 2054-34', '2054-35', '2054-36'];
        await checkExperimentationRecipeIds(
          priceSimplificationRecipeIds,
          'isSimplificationOn',
          this.properties,
          Experimentation,
        );

        await checkExperimentation(
          '2054-22',
          'isSimplificationOnV2',
          this.properties,
          Experimentation,
        );

        const nonBoldPercentageExpRecipeIds = ['2054-24', '2054-27', '2054-34', '2054-35', '2054-36'];
        await checkExperimentationRecipeIds(
          nonBoldPercentageExpRecipeIds,
          'simplificationNonBoldPercentageExp',
          this.properties,
          Experimentation,
        );

        const removeSaleExpRecipeIds = ['2054-26', '2054-27'];
        await checkExperimentationRecipeIds(
          removeSaleExpRecipeIds,
          'simplificationRemoveSaleExp',
          this.properties,
          Experimentation,
        );
      }
      this.setDeviceType();
      this.mapOnZone();
      this.loadRTODebounced = useDebounceFn(() => {
        this.isRecommendationDataAvailable = false;
        this.loadRTOCall();
      }, 500);
    }
  },
  async beforeMount() {
    if (!this.wishlistLoaded) {
      this.$store.commit('recommendationStore/setWishlistLoaded', true);
    }

    if (!this.$store.state?.recommendationStore?.pageId) {
      this.$store.commit(`recommendationStore/${SET_PAGE_ID}`);
    }

    let rviItems = [];
    const shouldFetchRecentlyViewedItems = this.rviDependent
      || this.resolvedPanelType === RVI
      || this.resolvedPanelType === DASHBOARD
      || isKillSwitchTrue('rviRelatedProsZonesEnabled', this.properties);

    const { Experimentation } = require('@vue-util/experimentation');
    await setKillSwitchUsingExperimentation(
      '1066-21',
      'isHeartFunctionallyEnabled',
      this.properties,
      Experimentation,
    );
    if ((this.zone === PDP_PAGE.PDP_ZONE_A || this.zone === PDP_PAGE.PDP_ZONE_G) && this.recommendationConfig?.productId) {
      addRecentlyViewedItems(this.recommendationConfig.productId);
    }
    if (shouldFetchRecentlyViewedItems) {
      try {
        rviItems = getRecentlyViewedItems();
        // filter out PWP and GWP products in case of RVI
        this.filterFetchedProducts = filterGWPAndPWPProducts;
        const { properties } = await useExperimentationForRvi({
          _properties: this.properties,
          expSdk: Experimentation,
        });
        await checkExperimentation(
          '2483-22',
          'redesignCoreCarouselEnabled',
          properties,
          Experimentation,
        );

        this.isExpNumberOfItemsMcomOn = isMcom() && properties.redesignCoreCarouselEnabled;

        // if Mcom Only
        if (isMcom()) {
          await checkExperimentation(
            '2483-21',
            'colorSwatchExp',
            properties,
            Experimentation,
          );
        }

        // for both Mcom and bcom
        if (this.priceSimplificationModeEnabled) {
          const priceSimplificationRecipeIds = ['2054-21', '2054-22', '2054-23', '2054-24', '2054-25', '2054-26', '2054-27', ' 2054-34', '2054-35', '2054-36'];
          await checkExperimentationRecipeIds(
            priceSimplificationRecipeIds,
            'isSimplificationOn',
            properties,
            Experimentation,
          );

          await checkExperimentation(
            '2054-22',
            'isSimplificationOnV2',
            properties,
            Experimentation,
          );

          const nonBoldPercentageExpRecipeIds = ['2054-24', '2054-27', '2054-34', '2054-35', '2054-36'];
          await checkExperimentationRecipeIds(
            nonBoldPercentageExpRecipeIds,
            'simplificationNonBoldPercentageExp',
            properties,
            Experimentation,
          );

          const removeSaleExpRecipeIds = ['2054-26', '2054-27'];
          await checkExperimentationRecipeIds(
            removeSaleExpRecipeIds,
            'simplificationRemoveSaleExp',
            properties,
            Experimentation,
          );
        }
        this.properties = properties;
      } catch {
        // 'Error getting recently viewed items'
      }
    }
    if (this.resolvedPanelType === POOL) {
      this.isRecommendationDataAvailable = true;
    } else if (this.resolvedPanelType === RVI) {
      if (rviItems.length > 0) {
        this.productIds = rviItems;
        this.isRecommendationDataAvailable = true;
        this.resolveRviType();
      }
      this.loading = false;
    } else if (
      this.resolvedPanelType === DASHBOARD
      && rviItems.length >= MIN_ITEMS_AMOUNT_FOR_DASHBOARD_RVI
    ) {
      this.productIds = rviItems;
      this.isRecommendationDataAvailable = true;
      this.resolveDashboardType();
      this.loading = false;
    } else if (
      isKillSwitchTrue('rviRelatedProsZonesEnabled', this.properties)
      && this.rviDependent
    ) {
      this.extraConfig = {};
      const rviProductId = rviItems.length > 0 ? rviItems[0] : '-9';
      this.extraConfig.productId = this.zone === SEARCH_PAGE.SEARCH_ZONE_A
        ? this.panelConfig.meta?.data?.productID[0] : rviProductId;
      this.extraConfig.recentlyViewedItems = rviItems.length > 0 ? rviItems.slice(0, 30).join('|') : '';
    }

    if (this.panelConfig.meta?.product) {
      const { product } = this.panelConfig.meta;
      this.extraConfig.brandName = this.zone.includes('ATBPage') ? '' : product.detail.brand.name;
      this.extraConfig.productName = product.detail.name;
      this.extraConfig.imageId = getImageId(product, this.zone);
      this.extraConfig.productType = this.zone.includes('ATBPage') ? product.productCategoryName : product.detail.typeName;
      this.extraConfig.productColorMap = getProductColorMap(product, this.zone);
    }
    if (this.zone && this.zone.includes('ATBPage')) {
      this.extraConfig.maxRecommendations = 75;
    }

    if ([PDP_PAGE.PDP_ZONE_F, PDP_PAGE.PDP_ZONE_H, PDP_PAGE.PDP_ZONE_I].includes(this.zone)) {
      this.extraConfig.maxRecommendations = 18;
    }

    if (this.zone === BAG_PAGE.SB_ZONE_A && this.bagItems) {
      this.extraConfig = this.getExtraBagConfig(this.bagItems.items);
    }
    if (this.linkUrl !== '') {
      this.extraConfig.url = this.linkUrl;
    }
    this.extraConfig.trafficOrigin = getURLParameter('m_sc');
    this.extraConfig.trafficSubChannel = getURLParameter('m_sb');
    this.extraConfig.pageId = this.$store.state.recommendationStore.pageId;

    if ([BROWSE_PAGE.BROWSE_ZONE_A, BROWSE_PAGE.BROWSE_ZONE_C].includes(this.zone)) {
      this.extraConfig.productId = this.panelConfig.meta?.data?.productID?.[0] || rviItems?.[0] || '-9';
    }

    this.extraConfig = { ...this.extraConfig, ...getGuids() };

    const shouldFetchRTOData = ((this.resolvedPanelType === DASHBOARD
      && rviItems.length < MIN_ITEMS_AMOUNT_FOR_DASHBOARD_RVI)
      || (this.resolvedPanelType === PROS && !this.rviDependent)
      || (this.resolvedPanelType === PROS
        && this.rviDependent
        && rviItems.length > 0))
      && this.isZoneEnabled(this.zone);
    if (shouldFetchRTOData && lazyloadConfig.removeLazyLoad[this.recommendationConfig?.zone]) {
      await this.loadRTOCall();
    }
  },
  mounted() {
    window.addEventListener('resize', this.onScreenChange);
    window.addEventListener('orientationchange', this.onScreenChange);

    if (this.resolvedPanelType === PROS && !lazyloadConfig.removeLazyLoad[this.recommendationConfig?.zone]) {
      const loadRTOCallback = async () => {
        await this.loadRTOCall();
      };
      onElementWillBeVisible(document.querySelector(`.pros-collection-${this.recommendationConfig?.zone}`), loadRTOCallback, 1200);
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onScreenChange);
    window.removeEventListener('orientationchange', this.onScreenChange);
  },
  methods: {
    isZoneEnabled(zoneName) {
      return (
        this.resolvedPanelType === RVI
        || this.resolvedPanelType === POOL
        || isKillSwitchTrue(
          killswitchFromZoneName(zoneName),
          this.properties,
          true,
          // !isZoneInBrowseList(this.zone),
        )
      );
    },
    mapOnZone() {
      if (!this.zone) {
        return;
      }

      if (!this.isZoneEnabled(this.zone)) {
        this.loading = false;
        this.$emit('dataLoadStatusEvent', true);

        return;
      }

      const zoneMappedConfig = prosCollectionZoneConfigMap(
        this.entryPoint,
        this.zone,
        {
          isExpNumberOfItemsMcomOn: this.isExpNumberOfItemsMcomOn,
        },
      );

      // check if isExpNumberOfItemsMcomOn is true and we have to override the large number of items for MCOM in panelConfig
      const basePanelConfig = removeUndefinedValuesFromObject(this.panelConfig);
      const isOverrideNumberOfItems = this.isExpNumberOfItemsMcomOn
        && Number(zoneMappedConfig.numberOfItems?.[LARGE])
        && Number(basePanelConfig.numberOfItems?.[LARGE]);
      const updatedPanelConfigNumberOfItems = isOverrideNumberOfItems
        ? { ...basePanelConfig.numberOfItems, [LARGE]: zoneMappedConfig.numberOfItems[LARGE] }
        : basePanelConfig.numberOfItems || {};

      const {
        componentType,
        panelType,
        numberOfItems,
        maxProduct,
        zoneLevelCustomClass,
        rviDependent,
        categoriesToExclude = [],
        linkName,
        linkUrl,
        getLinkUrl,
        transformer,
        extraRowGap,
        customHeaderType,
        headerTabIndex,
        headerAriaLabel,
      } = merge(
        zoneMappedConfig,
        {
          ...basePanelConfig,
          numberOfItems: updatedPanelConfigNumberOfItems,
        },
      );

      if (categoriesToExclude.includes(this.recommendationConfig.categoryId)) {
        return;
      }

      this.resolvedComponentType = componentType;
      this.resolvedPanelType = panelType;
      this.resolvedNumberOfItems = numberOfItems;
      this.rviDependent = rviDependent;
      this.linkName = linkName;
      this.transformer = transformer;
      this.linkUrl = this.linkUrl ?? linkUrl;
      this.getLinkUrl = getLinkUrl;
      this.extraRowGap = extraRowGap;
      this.maxProduct = maxProduct;
      this.zoneLevelCustomClass = zoneLevelCustomClass;
      this.customHeaderType = customHeaderType;
      this.headerTabIndex = headerTabIndex;
      this.headerAriaLabel = headerAriaLabel;
    },
    onScreenChange() {
      this.setDeviceType();
      this.mapOnZone();
    },
    resolveDashboardType() {
      this.resolvedComponentType = PROS_DASHBOARD;
    },
    resolveProsType() {
      if (this.recommendationData?.content) {
        this.resolvedComponentType = this.deviceType === SMALL
          ? PROS_CONTENT_CAROUSEL
          : PROS_CONTENT_COLLECTION;
      }
    },
    resolveRviType() {
      this.resolvedComponentType = RVI_CAROUSEL;
    },
    setDeviceType() {
      this.deviceType = getDeviceType();
    },
    updateRecommendationData() {
      try {
        if (this.recommendationData?.recommendedIdsList?.length > 0) {
          this.isRecommendationDataAvailable = true;
        } else {
          this.$emit('dataLoadStatusEvent', false);
        }
        this.loading = false;
      } catch (e) {
        this.loading = false;
        this.$emit('dataLoadStatusEvent', false);
        throw new Error(`not valid recommendationData: ${e}`);
      }
    },
    getExtraBagConfig(items) {
      const productColorMap = items
        .map(({ product }) => {
          const {
            id,
            traits: {
              colors: [c],
            },
          } = product;
          return `${id}_${c}`;
        })
        .join('|');

      const {
        product: {
          id: productId,
          detail: { defaultCategoryId: categoryId },
        },
      } = items.at(-1);

      return {
        productColorMap,
        productId,
        categoryId,
      };
    },
    async loadRTOCall() {
      const { Experimentation } = require('@vue-util/experimentation');
      try {
        const testZone = getURLParameter('testZone');
        const { config, properties } = await updateConfig({
          _config: {
            ...this.recommendationConfig,
            ...this.extraConfig,
            ...{ url: this.linkUrl },
            ...(testZone && { zone: testZone }),
          },
          _properties: this.properties,
          expSdk: Experimentation,
        });
        this.properties = properties;

        this.workflow = getWorkflow(config, this.zone);
        if (isPdpZone(this.zone) && this.zoneRecommendationMap[this.workflow]) {
          this.recommendationData = this.zoneRecommendationMap[this.workflow];
          this.isRecommendationDataAvailable = true;
        } else {
          useProsCollection({
            config,
            properties,
            store: this.$store,
          });
          this.$store.commit('recommendationStore/setPanelLoadedState', {
            workflow: this.workflow,
            isLoaded: false,
          });
        }
      } catch (e) {
        this.loading = false;
        throw new Error(`useProsCollection fail : ${e}`);
      }
    },
  },
};
</script>

<style lang="scss">
@import '~@core/csg/src/scss/typography/typography-mixins.scss';
@import '~@core/csg/src/scss/encodedIcons/mcom/review-star-filled-small.scss';

.pros-collection {
  max-width: 1600px;

  .wishlist-heart-large-card,
  .wishlist-heart-small-card {
    top: 0.25rem;
    right: 0.25rem;
  }

  .toast {
    position: absolute;
  }

  @include breakpoint(large up) {

    .wishlist-heart-large-card,
    .wishlist-heart-small-card {
      top: 0.5rem;
      right: 0.5rem;
    }
  }

  @include breakpoint(medium down) {
    .v-carousel .v-button-wrapper {
      display: none;
    }
  }

  .product-thumbnail-container {
    .vertical-alignment {
      .product-description {
        margin-top: rem-calc(12);
      }
    }
  }

  &.rating-size-small {
    .product-description {
      .rating {
        @include body(small);
        // fixed moving of pros-collection container on MEW
        line-height: inherit !important;
      }
    }
  }

  .product-thumbnail-container .rating {
    flex-wrap: wrap;

    fieldset {
      white-space: nowrap;
    }
  }
}
</style>

<style lang="scss" brand="mcom">

div.pros-collection.prod-name-one-line .description-spacing{
        div {
          &:nth-child(2) {
            display: -webkit-box;
            -webkit-box-orient: vertical;
            overflow: hidden;
            -webkit-line-clamp: 1;
            line-clamp: 1; /* fallback for browsers supporting CSS-overflow-4 */
          }
        }
  }

  div.pros-collection.prod-name-two-line .description-spacing{
        div {
          &:nth-child(2) {
            display: -webkit-box;
            -webkit-box-orient: vertical;
            overflow: hidden;
            -webkit-line-clamp: 2;
            line-clamp: 2; /* fallback for browsers supporting CSS-overflow-4 */
          }
        }
  }

div.pros-collection {
  .pros-vertical {
    .description-spacing {
      .product-brand {
        display: -webkit-box;
        -webkit-line-clamp: v-bind(lineClamp);
        -webkit-box-orient: vertical;
        overflow: hidden;
        // fallback for CSS-overflow-4
        line-clamp: v-bind(lineClamp);
      }

    }
  }
}
</style>

<style lang="scss" brand="bcom">
div.pros-collection {
  .pricing .price-reg {
    font-weight: $price-reg-font-weight;
  }
}
</style>
